/**
 * AB Test Dashboard
 *
 * Comprehensive admin dashboard for managing and monitoring A/B tests.
 * Displays experiments, statistical analysis, interactions, and charts.
 *
 * Features:
 * - Live experiment monitoring
 * - Statistical significance indicators
 * - Conversion rate charts
 * - Interaction warnings
 * - Sample size calculator
 *
 * Part of MVP 1.10 Week 4 Features
 *
 * Security: Fixed January 2026 - Now uses services/api.js with automatic token refresh
 * Authentication: Bearer token required for all endpoints
 * Authorization: Admin-only access via route loader
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  BarChart3,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp as _TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Calculator
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart as _LineChart,
  Line as _Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import DashboardLayout from '../layout/DashboardLayout';

// API client functions with automatic token refresh
// Uses services/api.js for authentication and session management

const fetchActiveExperiments = async () => {
  return await apiRequest('/api/v1/ab-tests/active', {
    method: 'GET'
  });
};

const fetchExperimentAnalysis = async (experimentId) => {
  return await apiRequest(`/api/v1/ab-tests/${experimentId}/analysis`, {
    method: 'GET'
  });
};

const fetchExperimentStats = async (experimentId) => {
  return await apiRequest(`/api/v1/ab-tests/${experimentId}/stats`, {
    method: 'GET'
  });
};

const fetchInteractions = async () => {
  return await apiRequest('/api/v1/ab-tests/interactions', {
    method: 'GET'
  });
};

const calculateSampleSize = async (params) => {
  return await apiRequest('/api/v1/ab-tests/sample-size', {
    method: 'POST',
    body: JSON.stringify(params)
  });
};

export function ABTestDashboard() {
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [stats, setStats] = useState(null);
  const [interactions, setInteractions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('experiments'); // experiments, analysis, interactions, calculator

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load experiments and interactions in parallel
      const [experimentsData, interactionsData] = await Promise.all([
        fetchActiveExperiments(),
        fetchInteractions()
      ]);

      setExperiments(experimentsData);
      setInteractions(interactionsData);

      // Auto-select first experiment if available
      if (experimentsData.length > 0 && !selectedExperiment) {
        await selectExperiment(experimentsData[0]);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedExperiment]);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const selectExperiment = async (_experiment) => {
    setSelectedExperiment(_experiment);
    setLoading(true);

    try {
      // Load analysis and stats for selected experiment
      const [analysisData, statsData] = await Promise.all([
        fetchExperimentAnalysis(_experiment.id),
        fetchExperimentStats(_experiment.id)
      ]);

      setAnalysis(analysisData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading experiment data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && experiments.length === 0) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="ab-tests"
        onNavigate={handleNavigate}
        user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <RefreshCw style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
          <span style={{ marginLeft: 'var(--space-3)', fontSize: 'var(--font-size-lg)' }}>Loading dashboard...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="ab-tests"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            A/B Tests
          </h1>
          <button
            onClick={loadDashboardData}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-4)',
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-white)',
              backgroundColor: 'var(--color-primary)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
              gap: 'var(--space-2)'
            }}
          >
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex space-x-4 border-b bg-white px-4 -mx-6">
          <TabButton
            active={activeTab === 'experiments'}
            onClick={() => setActiveTab('experiments')}
            icon={Activity}
            label="Experiments"
            badge={experiments.length}
          />
          <TabButton
            active={activeTab === 'analysis'}
            onClick={() => setActiveTab('analysis')}
            icon={BarChart3}
            label="Statistical Analysis"
            disabled={!selectedExperiment}
          />
          <TabButton
            active={activeTab === 'interactions'}
            onClick={() => setActiveTab('interactions')}
            icon={AlertTriangle}
            label="Interactions"
            badge={interactions?.hasInteractions ? '!' : null}
            badgeColor="red"
          />
          <TabButton
            active={activeTab === 'calculator'}
            onClick={() => setActiveTab('calculator')}
            icon={Calculator}
            label="Sample Size"
          />
        </div>

        {/* Content */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {activeTab === 'experiments' && (
          <ExperimentsTab
            experiments={experiments}
            selectedExperiment={selectedExperiment}
            onSelectExperiment={selectExperiment}
            stats={stats}
          />
        )}

        {activeTab === 'analysis' && selectedExperiment && (
          <AnalysisTab
            experiment={selectedExperiment}
            analysis={analysis}
            stats={stats}
            loading={loading}
          />
        )}

        {activeTab === 'interactions' && (
          <InteractionsTab interactions={interactions} />
        )}

        {activeTab === 'calculator' && <SampleSizeCalculator />}
      </div>
    </DashboardLayout>
  );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label, badge, badgeColor = 'blue', disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center px-4 py-3 border-b-2 font-medium text-sm transition-colors
        ${
          active
            ? 'border-blue-600 text-blue-600'
            : disabled
            ? 'border-transparent text-gray-400 cursor-not-allowed'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }
      `}
    >
      <Icon className="w-4 h-4 mr-2" />
      {label}
      {badge && (
        <span
          className={`
            ml-2 px-2 py-0.5 text-xs font-semibold rounded-full
            ${badgeColor === 'red' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}
          `}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// Experiments Tab
function ExperimentsTab({ experiments, selectedExperiment, onSelectExperiment, stats }) {
  if (experiments.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No active experiments found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Experiment List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Active Experiments</h2>
        {experiments.map((exp) => (
          <div
            key={exp.id}
            onClick={() => onSelectExperiment(exp)}
            className={`
              p-6 bg-white rounded-lg border-2 cursor-pointer transition-all
              ${
                selectedExperiment?.id === exp.id
                  ? 'border-blue-600 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }
            `}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900">{exp.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{exp.description}</p>
                <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                  <span>
                    Started: {new Date(exp.start_date).toLocaleDateString()}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                    {exp.status}
                  </span>
                </div>
              </div>
              {selectedExperiment?.id === exp.id && (
                <CheckCircle className="w-6 h-6 text-blue-600" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      {selectedExperiment && stats && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Quick Stats</h2>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">{selectedExperiment.name}</h3>
            <div className="space-y-4">
              {stats.variants?.map((variant) => (
                <div key={variant.variant} className="border-b pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 capitalize">
                      {variant.variant}
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      {((variant.conversion_rate || 0) * 100).toFixed(2)}%
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {variant.conversions} conversions / {variant.sample_size} users
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Analysis Tab (to be continued in next file due to size)
function AnalysisTab({ experiment: _experiment, analysis, stats, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No analysis data available</p>
      </div>
    );
  }

  // Prepare chart data
  const chartData = stats?.variants?.map((v) => ({
    name: v.variant.charAt(0).toUpperCase() + v.variant.slice(1),
    'Conversion Rate': ((v.conversion_rate || 0) * 100).toFixed(2),
    'Sample Size': v.sample_size
  })) || [];

  return (
    <div className="space-y-6">
      {/* Overall Significance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Overall Results</h2>
        <SignificanceBadge significance={analysis.overallSignificance} large />
      </div>

      {/* Conversion Rate Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversion Rates</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis label={{ value: 'Conversion Rate (%)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Conversion Rate" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pairwise Comparisons */}
      {analysis.pairwiseComparisons?.map((comparison, index) => (
        <div key={index} className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {comparison.controlVariant} vs {comparison.testVariant}
          </h3>

          {/* Significance */}
          <div className="mb-4">
            <SignificanceBadge significance={comparison.significance.conclusion} />
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              label="Chi-Square"
              value={(comparison.statistics?.chiSquare?.statistic || 0).toFixed(4)}
              sublabel={`p = ${(comparison.statistics?.chiSquare?.pValue || 0).toFixed(4)}`}
            />
            <StatCard
              label="Effect Size"
              value={(comparison.statistics?.effectSize?.cohensD || 0).toFixed(3)}
              sublabel={comparison.statistics?.effectSize?.interpretation || 'N/A'}
            />
            <StatCard
              label="Lift"
              value={`${(comparison.statistics?.lift?.percentage || 0).toFixed(2)}%`}
              sublabel={(comparison.statistics?.lift?.relative || 0) > 0 ? 'improvement' : 'decline'}
              trend={(comparison.statistics?.lift?.relative || 0) > 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Corrected α"
              value={(comparison.significance?.correctedAlpha || 0).toFixed(3)}
              sublabel={`${comparison.significance?.numComparisons || 0} comparisons`}
            />
          </div>

          {/* Confidence Intervals */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Control</p>
              <p className="text-lg font-semibold">
                {((comparison.controlData?.conversionRate || 0) * 100).toFixed(2)}%
              </p>
              <p className="text-xs text-gray-500">
                95% CI: [
                {((comparison.controlData?.confidenceInterval?.lower || 0) * 100).toFixed(2)}%,
                {((comparison.controlData?.confidenceInterval?.upper || 0) * 100).toFixed(2)}%]
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Test Variant</p>
              <p className="text-lg font-semibold">
                {((comparison.testData?.conversionRate || 0) * 100).toFixed(2)}%
              </p>
              <p className="text-xs text-gray-500">
                95% CI: [
                {((comparison.testData?.confidenceInterval?.lower || 0) * 100).toFixed(2)}%,
                {((comparison.testData?.confidenceInterval?.upper || 0) * 100).toFixed(2)}%]
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Interactions Tab
function InteractionsTab({ interactions }) {
  if (!interactions) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!interactions.hasInteractions) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <CheckCircle className="w-12 h-12 mx-auto text-green-600 mb-4" />
        <p className="text-gray-900 font-semibold text-lg">No Interactions Detected</p>
        <p className="text-gray-600 mt-2">{interactions.message || 'All experiments are independent'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <div className={`
        p-6 rounded-lg border-2
        ${interactions.overallSeverity === 'critical' ? 'bg-red-50 border-red-500' :
          interactions.overallSeverity === 'high' ? 'bg-orange-50 border-orange-500' :
          interactions.overallSeverity === 'medium' ? 'bg-yellow-50 border-yellow-500' :
          'bg-blue-50 border-blue-500'}
      `}>
        <div className="flex items-center mb-2">
          <AlertTriangle className={`
            w-6 h-6 mr-3
            ${interactions.overallSeverity === 'critical' ? 'text-red-600' :
              interactions.overallSeverity === 'high' ? 'text-orange-600' :
              interactions.overallSeverity === 'medium' ? 'text-yellow-600' :
              'text-blue-600'}
          `} />
          <h2 className="text-xl font-semibold text-gray-900">
            {interactions.overallSeverity.toUpperCase()} Severity
          </h2>
        </div>
        <p className="text-gray-700">{interactions.recommendation}</p>
      </div>

      {/* Individual Interactions */}
      {interactions.interactions?.map((interaction, index) => (
        <div key={index} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {interaction.experimentA.name} ↔ {interaction.experimentB.name}
            </h3>
            <SeverityBadge severity={interaction.severity} />
          </div>

          {/* Overlap Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Experiment A Overlap</p>
              <p className="text-2xl font-bold text-gray-900">
                {interaction.experimentA.overlapPercentage.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500">
                {interaction.experimentA.overlapCount} / {interaction.experimentA.totalAssignments} users
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Experiment B Overlap</p>
              <p className="text-2xl font-bold text-gray-900">
                {interaction.experimentB.overlapPercentage.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500">
                {interaction.experimentB.overlapCount} / {interaction.experimentB.totalAssignments} users
              </p>
            </div>
          </div>

          {/* Warnings */}
          {interaction.warnings?.length > 0 && (
            <div className="space-y-2 mb-4">
              {interaction.warnings.map((warning, i) => (
                <div key={i} className={`
                  flex items-start p-3 rounded
                  ${warning.level === 'error' ? 'bg-red-50 text-red-800' :
                    warning.level === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                    'bg-blue-50 text-blue-800'}
                `}>
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{warning.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {interaction.recommendations?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Recommendations:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                {interaction.recommendations.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Sample Size Calculator Tab
function SampleSizeCalculator() {
  const [baselineRate, setBaselineRate] = useState(0.10);
  const [mde, setMde] = useState(0.20);
  const [alpha, setAlpha] = useState(0.05);
  const [power, setPower] = useState(0.80);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const data = await calculateSampleSize({
        baselineRate,
        minimumDetectableEffect: mde,
        alpha,
        power
      });
      setResult(data);
    } catch (err) {
      console.error('Error calculating sample size:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Calculator className="w-6 h-6 mr-3 text-blue-600" />
          Sample Size Calculator
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Baseline Conversion Rate
            </label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={baselineRate}
              onChange={(e) => setBaselineRate(parseFloat(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              E.g., 0.10 for 10% conversion rate
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Detectable Effect (MDE)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={mde}
              onChange={(e) => setMde(parseFloat(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              E.g., 0.20 for 20% relative lift
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Significance Level (α)
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={alpha}
                onChange={(e) => setAlpha(parseFloat(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statistical Power
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={power}
                onChange={(e) => setPower(parseFloat(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <button
            onClick={calculate}
            disabled={loading}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
          >
            {loading ? 'Calculating...' : 'Calculate Sample Size'}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Sample Size Per Variant</p>
              <p className="text-3xl font-bold text-blue-600">
                {result.sampleSizePerVariant.toLocaleString()}
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Sample Size (Both Variants)</p>
              <p className="text-2xl font-bold text-gray-900">
                {result.totalSampleSize.toLocaleString()}
              </p>
            </div>

            <div className="text-sm text-gray-600">
              <p className="font-medium mb-2">Assumptions:</p>
              <ul className="space-y-1">
                <li>• Baseline rate: {(result.assumptions.baselineRate * 100).toFixed(1)}%</li>
                <li>• Expected variant rate: {(result.assumptions.expectedVariantRate * 100).toFixed(1)}%</li>
                <li>• Minimum detectable effect: {(result.assumptions.minimumDetectableEffect * 100).toFixed(1)}%</li>
                <li>• Significance level: {result.assumptions.alpha}</li>
                <li>• Statistical power: {result.assumptions.power}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function SignificanceBadge({ significance, large = false }) {
  const config = {
    'WINNER_FOUND': { color: 'green', icon: CheckCircle, text: 'Winner Found' },
    'VARIANT_PERFORMS_WORSE': { color: 'red', icon: XCircle, text: 'Variant Worse' },
    'PROMISING_BUT_NEEDS_MORE_DATA': { color: 'yellow', icon: _TrendingUp, text: 'Promising' },
    'INCONCLUSIVE': { color: 'gray', icon: Minus, text: 'Inconclusive' },
    'WINNER': { color: 'green', icon: _TrendingUp, text: 'Winner' },
    'LOSER': { color: 'red', icon: TrendingDown, text: 'Loser' },
    'SIGNIFICANT_BUT_SMALL': { color: 'yellow', icon: Minus, text: 'Significant but Small' },
    'PROMISING': { color: 'blue', icon: _TrendingUp, text: 'Promising' }
  };

  const { color, icon: Icon, text } = config[significance] || config['INCONCLUSIVE'];

  const bgColor = `bg-${color}-100`;
  const textColor = `text-${color}-800`;

  return (
    <div className={`inline-flex items-center ${bgColor} ${textColor} rounded-full ${large ? 'px-6 py-3 text-lg' : 'px-4 py-2 text-sm'} font-semibold`}>
      <Icon className={`${large ? 'w-6 h-6' : 'w-4 h-4'} mr-2`} />
      {text}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const colors = {
    'critical': 'bg-red-100 text-red-800',
    'high': 'bg-orange-100 text-orange-800',
    'medium': 'bg-yellow-100 text-yellow-800',
    'low': 'bg-blue-100 text-blue-800',
    'none': 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors[severity] || colors.none}`}>
      {severity.toUpperCase()}
    </span>
  );
}

function StatCard({ label, value, sublabel, trend }) {
  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <p className="text-xs text-gray-600 mb-1">{label}</p>
      <div className="flex items-center">
        <p className="text-xl font-bold text-gray-900">{value}</p>
        {trend && (
          trend === 'up' ? (
            <_TrendingUp className="w-4 h-4 ml-2 text-green-600" />
          ) : (
            <TrendingDown className="w-4 h-4 ml-2 text-red-600" />
          )
        )}
      </div>
      {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
    </div>
  );
}

export default ABTestDashboard;
